import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const fits = [
  "Want to register and control your own domain",
  "Need to understand hosting rather than guess your way through it",
  "Want to learn HTML and CSS from the ground up",
  "Plan to use WordPress, but want to configure it correctly",
  "Care about accessibility, performance, and search visibility",
  "Want a site you can move, maintain, and grow over time",
];

const notFits = [
  "Already manage servers comfortably and ship advanced WordPress builds regularly",
  "Want a shortcut-only guide with no interest in understanding the reasons behind the steps",
  "Prefer a platform-locked builder specifically because you don't care about portability",
];

const audiences = [
  { title: "Beginners & Students", desc: "If you've never written a line of code, this is your starting line. We explain the 'why' before the 'how'.", icon: FiIcons.FiUser },
  { title: "Small Business Owners", desc: "Stop paying high monthly fees for limited platforms. Learn to build and own your primary digital asset.", icon: FiIcons.FiBriefcase },
  { title: "Freelancers & Consultants", desc: "Transition from basic site configuration to offering robust, custom web builds for your clients.", icon: FiIcons.FiMonitor },
  { title: "Non-Technical Founders", desc: "Even if you plan to hire an agency later, understanding the foundation ensures you won't get ripped off.", icon: FiIcons.FiTarget },
];

export default function Audience() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">Who This Book Is For</h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
          This book is for people who need a professional website but don't want to hand control of that website to a closed platform, a vague tutorial, or a pile of disconnected tools.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {audiences.map((item, idx) => (
          <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
              <SafeIcon icon={item.icon} className="text-blue-600 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
            <p className="text-slate-600 leading-relaxed">{item.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 md:p-12 mb-16">
        <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl">
          It is also for readers who have tried to build a website before and walked away feeling that something was missing — maybe the site went live, but it didn't feel truly owned, was hard to maintain, or unclear in structure. This book is designed to fix that.
        </p>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiThumbsUp} className="text-green-500" /> This book is a strong fit if you:
            </h3>
            <ul className="space-y-3">
              {fits.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiThumbsDown} className="text-red-500" /> This book is not the right fit if you:
            </h3>
            <ul className="space-y-3">
              {notFits.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <SafeIcon icon={FiIcons.FiX} className="text-red-400 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 rounded-3xl p-10 md:p-14 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Ready to build a website you actually own?</h2>
        <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
          This book doesn't offer shortcuts. It offers clarity — and a foundation that holds up over time.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/learn" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-7 py-3.5 rounded-lg font-bold hover:bg-slate-100 transition-colors">
            See What You'll Learn <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
          <Link to="/buy" className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-lg font-bold hover:bg-blue-500 transition-colors">
            Buy the Book
          </Link>
        </div>
      </div>
    </div>
  );
}